// WP-1.2/1.3/1.4/1.7 (§18.5, EX-1, TX-4, FE-1/FE-2, EX-2, ST-2): Quick Expense/Income
// (amount, category, account — ≤3 interactions, SC-1) with a "More fields" reveal for the
// Detailed mode (party, note, date, tags) and, within Detailed, an optional split into
// multiple lines (TX-4/PJ-6) and optional fee legs (typed, net-of-fees — FE-2) with a live
// net preview. Category is a real CategoryPicker sourced from the §16.11 tree; the split
// lines' per-line categories stay free text (a line isn't itself an entity to pick from).
// WP-3.5 (SC-5, PJ-6, TX-4): income gains §18.5's own "against" segmented control (none /
// schedule item / receivable) for the common single-link case, and each split line MAY carry
// a project/schedule-item/receivable link for the "repeat rows {project|schedule|RP, amount}"
// multi-way case. WP-3.6 (PP-1): a non-split expense gains its own simple project picker —
// §18.5's Detailed Expense field list names this exact field ("project (optional -> cost
// link PP-1)"); a SPLIT expense stays category-only (no spec example splits a cost across
// projects the way PJ-6 splits revenue — surfaced as a scope trim, not invented).
import {
  FEE_TYPES,
  formatMoney,
  fromMinor,
  parseAmountInput,
  type CurrencyCode,
  type FeeType,
} from '@pfos/engine';
import { Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AccountPicker } from './AccountPicker';
import { AmountField, Field, ghostButtonInlineClass, inputClass, selectClass } from './FormKit';
import { CategoryPicker } from './CategoryPicker';
import { FormSheet } from './FormSheet';
import { PartyPicker } from './PartyPicker';
import { TagInput } from './TagInput';
import { useAccounts } from '../lib/accounts';
import { ApiError } from '../lib/api';
import { useParties } from '../lib/parties';
import { useProjects } from '../lib/projects';
import { useRps, useSettleRp } from '../lib/rps';
import { useScheduleItems } from '../lib/scheduleItems';
import { useProposeAllocationSuggestion } from '../lib/suggestions';
import { useRecordTransaction } from '../lib/transactions';

interface FormValues {
  accountId: string;
  category: string;
  party: string;
  note: string;
  date: string; // yyyy-mm-dd, detailed mode only
}

type SplitLinkType = 'none' | 'project' | 'scheduleItem' | 'receivable';

interface SplitLine {
  amount: string; // raw AmountField text
  category: string;
  linkType: SplitLinkType;
  linkId: string;
}

interface FeeRow {
  type: FeeType | '';
  amount: string; // raw AmountField text
}

type Against = 'none' | 'scheduleItem' | 'receivable';

const EMPTY_VALUES: FormValues = { accountId: '', category: '', party: '', note: '', date: '' };
const EMPTY_SPLIT_LINES: SplitLine[] = [
  { amount: '', category: '', linkType: 'none', linkId: '' },
  { amount: '', category: '', linkType: 'none', linkId: '' },
];
const EMPTY_FEE_ROWS: FeeRow[] = [{ type: '', amount: '' }];

export function TransactionForm({
  open,
  onOpenChange,
  initialType,
  initialParty,
  initialPartyId,
  initialScheduleItemId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** WP-3.4/S-14's own "Receive → Income form pre-linked" — the same form, opened pre-filled. */
  initialType?: 'expense' | 'income';
  initialParty?: string;
  initialPartyId?: string;
  initialScheduleItemId?: string;
}): React.JSX.Element {
  const { data: accounts } = useAccounts();
  const { data: parties } = useParties(false);
  const { data: projects } = useProjects();
  const { register, handleSubmit, watch, reset, setValue } = useForm<FormValues>({
    defaultValues: EMPTY_VALUES,
  });
  const [txType, setTxType] = useState<'expense' | 'income'>(initialType ?? 'expense');
  const [detailed, setDetailed] = useState(initialScheduleItemId !== undefined);
  const [split, setSplit] = useState(false);
  const [splitLines, setSplitLines] = useState<SplitLine[]>(EMPTY_SPLIT_LINES);
  const [hasFees, setHasFees] = useState(false);
  const [feeRows, setFeeRows] = useState<FeeRow[]>(EMPTY_FEE_ROWS);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [amount, setAmount] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [partyId, setPartyId] = useState(initialPartyId ?? '');
  const [against, setAgainst] = useState<Against>(
    initialScheduleItemId !== undefined ? 'scheduleItem' : 'none',
  );
  const [scheduleItemId, setScheduleItemId] = useState(initialScheduleItemId ?? '');
  const [receivableId, setReceivableId] = useState('');
  const [costProjectId, setCostProjectId] = useState(''); // WP-3.6 (PP-1): expense -> project cost link
  const recordMutation = useRecordTransaction();
  const settleMutation = useSettleRp(receivableId === '' ? 'none-selected' : receivableId);
  const proposeAllocationMutation = useProposeAllocationSuggestion();
  const accountId = watch('accountId');
  const account = (accounts ?? []).find((a) => a.id === accountId);
  const currency: CurrencyCode = account?.currency ?? 'PKR';

  const { data: scheduleItems } = useScheduleItems({ ...(partyId !== '' ? { partyId } : {}) });
  const { data: receivables } = useRps({ kind: 'receivable', status: 'open' });

  // §14-13 crash-safety: one idempotency key per open "compose session" — a retry after a
  // dropped response reuses it, so the server can't record the same entry twice.
  const idempotencyKey = useRef(crypto.randomUUID());

  useEffect(() => {
    if (open) {
      reset({ ...EMPTY_VALUES, party: initialParty ?? '' });
      setAmount('');
      setError(null);
      setDetailed(initialScheduleItemId !== undefined);
      setSplit(false);
      setSplitLines(EMPTY_SPLIT_LINES);
      setHasFees(false);
      setFeeRows(EMPTY_FEE_ROWS);
      setTagIds([]);
      setTxType(initialType ?? 'expense');
      setPartyId(initialPartyId ?? '');
      setAgainst(initialScheduleItemId !== undefined ? 'scheduleItem' : 'none');
      setScheduleItemId(initialScheduleItemId ?? '');
      setReceivableId('');
      setCostProjectId('');
      idempotencyKey.current = crypto.randomUUID();
    }
  }, [open, reset]);

  const totalParsed =
    amount.trim() === '' ? null : parseAmountInput(amount, { defaultCurrency: currency });
  const splitLineAmounts = splitLines.map((l) =>
    l.amount.trim() === '' ? null : parseAmountInput(l.amount, { defaultCurrency: currency }),
  );
  const splitSumMinor = splitLineAmounts.reduce<number | null>(
    (sum, p) => (sum === null || !p?.ok ? null : sum + p.money.minorUnits),
    0,
  );
  const remainderMinor =
    split && totalParsed?.ok === true && splitSumMinor !== null
      ? totalParsed.money.minorUnits - splitSumMinor
      : null;

  const usedFeeRows = feeRows.filter((f) => f.type !== '' || f.amount.trim() !== '');
  const feeAmounts = usedFeeRows.map((f) =>
    f.amount.trim() === '' ? null : parseAmountInput(f.amount, { defaultCurrency: currency }),
  );
  const feeTotalMinor = feeAmounts.reduce<number | null>(
    (sum, p) => (sum === null || !p?.ok ? null : sum + p.money.minorUnits),
    0,
  );
  const netMinor =
    hasFees && totalParsed?.ok === true && feeTotalMinor !== null
      ? txType === 'income'
        ? totalParsed.money.minorUnits - feeTotalMinor
        : totalParsed.money.minorUnits + feeTotalMinor
      : null;

  const eligibleScheduleItems = (scheduleItems ?? []).filter(
    (i) => i.currency === currency && i.status !== 'cancelled' && i.status !== 'paid',
  );
  const eligibleReceivables = (receivables ?? []).filter(
    (r) => r.currency === currency && (partyId === '' || r.partyId === partyId),
  );

  const onValid = handleSubmit(async (values) => {
    setError(null);
    if (account === undefined) {
      setError('Choose an account');
      return;
    }
    const parsed = parseAmountInput(amount, { defaultCurrency: account.currency });
    if (!parsed.ok) return; // AmountField already shows this inline

    if (!split && txType === 'income') {
      if (against === 'scheduleItem' && scheduleItemId === '') {
        setError('Pick a schedule item, or set "Against" to none');
        return;
      }
      if (against === 'receivable' && receivableId === '') {
        setError('Pick a receivable, or set "Against" to none');
        return;
      }
    }

    let lines:
      { amountMinor: number; category?: string; linkType?: string; linkId?: string }[] | undefined;
    if (split) {
      const parsedLines = splitLines.map((l) => ({
        parsed: parseAmountInput(l.amount, { defaultCurrency: account.currency }),
        category: l.category,
        linkType: l.linkType,
        linkId: l.linkId,
      }));
      const bad = parsedLines.find((l) => !l.parsed.ok);
      if (bad !== undefined) return; // each line's own AmountField already shows its error
      if (remainderMinor !== 0) {
        setError('Split lines must add up to the total amount');
        return;
      }
      const unlinked = parsedLines.find((l) => l.linkType !== 'none' && l.linkId === '');
      if (unlinked !== undefined) {
        setError('Pick an entity for every linked split line, or set it back to "None"');
        return;
      }
      lines = parsedLines.map((l) => ({
        amountMinor: l.parsed.ok ? l.parsed.money.minorUnits : 0,
        ...(l.category !== '' ? { category: l.category } : {}),
        ...(l.linkType !== 'none' ? { linkType: l.linkType, linkId: l.linkId } : {}),
      }));
    }

    let feeLegs: { type: FeeType; amountMinor: number }[] | undefined;
    if (hasFees) {
      const parsedFees = usedFeeRows.map((f) => ({
        type: f.type,
        parsed: parseAmountInput(f.amount, { defaultCurrency: account.currency }),
      }));
      const badFee = parsedFees.find((f) => f.type === '' || !f.parsed.ok);
      if (badFee !== undefined) {
        // A bad amount already shows its own inline error via AmountField; a missing type
        // does not, so that case gets the form-level banner instead.
        if (badFee.type === '') setError('Choose a fee type for each fee line');
        return;
      }
      feeLegs = parsedFees.map((f) => ({
        type: f.type as FeeType,
        amountMinor: f.parsed.ok ? f.parsed.money.minorUnits : 0,
      }));
    }

    try {
      const created = await recordMutation.mutateAsync({
        id: idempotencyKey.current,
        type: txType,
        accountId: account.id,
        amountMinor: parsed.money.minorUnits,
        ...(!split && values.category !== '' ? { category: values.category } : {}),
        ...(detailed && values.party !== '' ? { party: values.party } : {}),
        ...(detailed && partyId !== '' ? { partyId } : {}),
        ...(detailed && values.note !== '' ? { note: values.note } : {}),
        ...(detailed && values.date !== '' ? { at: new Date(values.date).toISOString() } : {}),
        ...(!split && txType === 'income' && against === 'scheduleItem' && scheduleItemId !== ''
          ? { scheduleItemId }
          : {}),
        // PP-1: "expenses link to projects" — §18.5's own Detailed Expense field list.
        ...(!split && txType === 'expense' && costProjectId !== ''
          ? { projectId: costProjectId }
          : {}),
        ...(lines !== undefined ? { lines } : {}),
        ...(feeLegs !== undefined ? { feeLegs } : {}),
        ...(detailed && tagIds.length > 0 ? { tagIds } : {}),
      });
      // AD-3 "New-income trigger": recording new income offers an allocation suggestion.
      // Fire-and-forget — never awaited, never lets a failed propose call block or fail the
      // transaction-recording flow itself.
      if (txType === 'income') {
        proposeAllocationMutation
          .mutateAsync({ incomeMinor: parsed.money.minorUnits, currency: account.currency })
          .catch(() => {
            /* non-blocking: a failed suggestion proposal must never affect the recorded income */
          });
      }
      // SC-5/RP-4: "against a receivable" has no single top-level field (a split line is the
      // only in-payload settlement path, and TX-4's own schema requires >=2 lines) — so a
      // single-pick receivable settles via a second call, the same two-step Owed.tsx already uses.
      if (!split && txType === 'income' && against === 'receivable' && receivableId !== '') {
        await settleMutation.mutateAsync({
          amountMinor: parsed.money.minorUnits,
          transactionId: created.id,
        });
      }
      onOpenChange(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not record the transaction');
    }
  });

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      title={txType === 'expense' ? 'Add expense' : 'Add income'}
      submitLabel="Record"
      submitting={recordMutation.isPending || settleMutation.isPending}
      onSubmit={() => {
        void onValid();
      }}
    >
      <div className="mb-3 flex gap-2" role="radiogroup" aria-label="Transaction type">
        {(['expense', 'income'] as const).map((t) => (
          <button
            key={t}
            type="button"
            role="radio"
            aria-checked={txType === t}
            onClick={() => {
              setTxType(t);
            }}
            className={`flex-1 rounded-[var(--radius-control)] border px-3 py-1.5 text-sm capitalize ${
              txType === t
                ? 'border-primary bg-primary/10 font-medium text-primary'
                : 'border-border text-ink hover:bg-bg'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <Field label="Amount" htmlFor="tx-amount">
        <AmountField
          id="tx-amount"
          currency={currency}
          value={amount}
          onChange={setAmount}
          autoFocus
        />
      </Field>

      <Field label="Account" htmlFor="tx-account">
        <AccountPicker
          id="tx-account"
          value={accountId}
          onChange={(v) => {
            reset({ ...EMPTY_VALUES, accountId: v });
            setPartyId('');
            setAgainst('none');
            setScheduleItemId('');
            setReceivableId('');
            setCostProjectId('');
          }}
        />
      </Field>

      {!split && (
        <Field label="Category (optional)" htmlFor="tx-category">
          <CategoryPicker
            id="tx-category"
            kind={txType}
            value={watch('category')}
            onChange={(name) => {
              setValue('category', name);
            }}
          />
        </Field>
      )}

      {!detailed && (
        <button
          type="button"
          className="mb-3 text-sm text-primary hover:underline"
          onClick={() => {
            setDetailed(true);
          }}
        >
          More fields
        </button>
      )}

      {detailed && (
        <>
          <Field label="Party (optional link)" htmlFor="tx-party-id">
            <PartyPicker
              id="tx-party-id"
              value={partyId}
              onChange={(id) => {
                setPartyId(id);
                const matched = (parties ?? []).find((p) => p.id === id);
                if (matched !== undefined) setValue('party', matched.name);
                // WP-C1.9 hardening: eligibleScheduleItems/eligibleReceivables are both
                // party-filtered, so a schedule item or receivable already picked under the
                // OLD party must not silently survive under the new one (same reset
                // AccountPicker's own onChange already does on a currency-changing account
                // switch) — a project-linked split line is NOT party-filtered, so those are
                // deliberately left untouched.
                setAgainst('none');
                setScheduleItemId('');
                setReceivableId('');
                setSplitLines((prev) =>
                  prev.map((l) =>
                    l.linkType === 'scheduleItem' || l.linkType === 'receivable'
                      ? { ...l, linkType: 'none', linkId: '' }
                      : l,
                  ),
                );
              }}
            />
          </Field>
          <Field label="Party name" htmlFor="tx-party">
            <input id="tx-party" className={inputClass} {...register('party')} />
          </Field>
          <Field label="Note (optional)" htmlFor="tx-note">
            <input id="tx-note" className={inputClass} {...register('note')} />
          </Field>
          <Field label="Date (optional — defaults to now)" htmlFor="tx-date">
            <input id="tx-date" type="date" className={inputClass} {...register('date')} />
          </Field>
          <Field label="Tags (optional)" htmlFor="tx-tags">
            <TagInput id="tx-tags" value={tagIds} onChange={setTagIds} />
          </Field>

          {txType === 'income' && !split && (
            <Field label="Against (optional)" htmlFor="tx-against">
              <select
                id="tx-against"
                className={selectClass}
                value={against}
                onChange={(e) => {
                  setAgainst(e.target.value as Against);
                  setScheduleItemId('');
                  setReceivableId('');
                }}
              >
                <option value="none">None</option>
                <option value="scheduleItem">Schedule item</option>
                <option value="receivable">Receivable</option>
              </select>
            </Field>
          )}
          {txType === 'income' && !split && against === 'scheduleItem' && (
            <Field label="Schedule item" htmlFor="tx-against-schedule">
              <select
                id="tx-against-schedule"
                className={selectClass}
                value={scheduleItemId}
                onChange={(e) => {
                  setScheduleItemId(e.target.value);
                }}
              >
                <option value="">Select a schedule item…</option>
                {eligibleScheduleItems.map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.party} — {formatMoney(fromMinor(i.currency, i.remainingMinor))} remaining
                    {i.milestone !== undefined ? ` (${i.milestone})` : ''}
                  </option>
                ))}
              </select>
            </Field>
          )}
          {txType === 'income' && !split && against === 'receivable' && (
            <Field label="Receivable" htmlFor="tx-against-receivable">
              <select
                id="tx-against-receivable"
                className={selectClass}
                value={receivableId}
                onChange={(e) => {
                  setReceivableId(e.target.value);
                }}
              >
                <option value="">Select a receivable…</option>
                {eligibleReceivables.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.party} — {formatMoney(fromMinor(r.currency, r.amountMinor - r.settledMinor))}{' '}
                    remaining ({r.reason})
                  </option>
                ))}
              </select>
            </Field>
          )}
          {txType === 'expense' && !split && (
            <Field label="Project (optional — cost link, PP-1)" htmlFor="tx-cost-project">
              <select
                id="tx-cost-project"
                className={selectClass}
                value={costProjectId}
                onChange={(e) => {
                  setCostProjectId(e.target.value);
                }}
              >
                <option value="">No project</option>
                {(projects ?? [])
                  .filter((p) => p.currency === currency)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.title} — {p.party}
                    </option>
                  ))}
              </select>
            </Field>
          )}

          <label className="mb-3 flex items-center gap-2 text-sm text-ink" htmlFor="tx-split">
            <input
              id="tx-split"
              type="checkbox"
              checked={split}
              onChange={(e) => {
                setSplit(e.target.checked);
              }}
            />
            Split into multiple lines
          </label>

          {split && (
            <div className="mb-3 rounded-[var(--radius-control)] border border-border p-3">
              {splitLines.map((line, i) => (
                <div
                  key={`line-${String(i)}`}
                  className="mb-2 rounded-[var(--radius-control)] border border-border p-2"
                >
                  <div className="mb-2 flex gap-2">
                    <div className="flex-1">
                      <AmountField
                        id={`tx-split-amount-${String(i)}`}
                        ariaLabel={`Line ${String(i + 1)} amount`}
                        currency={currency}
                        value={line.amount}
                        onChange={(v) => {
                          setSplitLines((prev) =>
                            prev.map((l, j) => (j === i ? { ...l, amount: v } : l)),
                          );
                        }}
                      />
                    </div>
                    <input
                      className={`${inputClass} flex-1`}
                      placeholder="Category"
                      aria-label={`Line ${String(i + 1)} category`}
                      value={line.category}
                      onChange={(e) => {
                        const { value } = e.target;
                        setSplitLines((prev) =>
                          prev.map((l, j) => (j === i ? { ...l, category: value } : l)),
                        );
                      }}
                    />
                    {splitLines.length > 2 && (
                      <button
                        type="button"
                        aria-label={`Remove line ${String(i + 1)}`}
                        className="text-muted hover:text-danger"
                        onClick={() => {
                          setSplitLines((prev) => prev.filter((_, j) => j !== i));
                        }}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  {txType === 'income' && (
                    <div className="flex gap-2">
                      <select
                        className={`${selectClass} flex-1`}
                        aria-label={`Line ${String(i + 1)} link type`}
                        value={line.linkType}
                        onChange={(e) => {
                          const linkType = e.target.value as SplitLinkType;
                          setSplitLines((prev) =>
                            prev.map((l, j) => (j === i ? { ...l, linkType, linkId: '' } : l)),
                          );
                        }}
                      >
                        <option value="none">Not linked</option>
                        <option value="project">Project</option>
                        <option value="scheduleItem">Schedule item</option>
                        <option value="receivable">Receivable</option>
                      </select>
                      {line.linkType !== 'none' && (
                        <select
                          className={`${selectClass} flex-1`}
                          aria-label={`Line ${String(i + 1)} linked entity`}
                          value={line.linkId}
                          onChange={(e) => {
                            const { value } = e.target;
                            setSplitLines((prev) =>
                              prev.map((l, j) => (j === i ? { ...l, linkId: value } : l)),
                            );
                          }}
                        >
                          <option value="">Select…</option>
                          {line.linkType === 'project' &&
                            (projects ?? []).map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.title}
                              </option>
                            ))}
                          {line.linkType === 'scheduleItem' &&
                            eligibleScheduleItems.map((si) => (
                              <option key={si.id} value={si.id}>
                                {si.party} —{' '}
                                {formatMoney(fromMinor(si.currency, si.remainingMinor))} remaining
                              </option>
                            ))}
                          {line.linkType === 'receivable' &&
                            eligibleReceivables.map((r) => (
                              <option key={r.id} value={r.id}>
                                {r.party} —{' '}
                                {formatMoney(fromMinor(r.currency, r.amountMinor - r.settledMinor))}{' '}
                                remaining
                              </option>
                            ))}
                        </select>
                      )}
                    </div>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="mb-2 flex items-center gap-1 text-sm text-primary hover:underline"
                onClick={() => {
                  setSplitLines((prev) => [
                    ...prev,
                    { amount: '', category: '', linkType: 'none', linkId: '' },
                  ]);
                }}
              >
                <Plus size={14} /> Add line
              </button>
              <p className={`text-sm ${remainderMinor === 0 ? 'text-muted' : 'text-danger'}`}>
                {remainderMinor === null
                  ? 'Enter the total amount and each line'
                  : remainderMinor === 0
                    ? 'Lines add up to the total'
                    : `Remaining: ${formatMoney(fromMinor(currency, remainderMinor))}`}
              </p>
            </div>
          )}

          <label className="mb-3 flex items-center gap-2 text-sm text-ink" htmlFor="tx-has-fees">
            <input
              id="tx-has-fees"
              type="checkbox"
              checked={hasFees}
              onChange={(e) => {
                setHasFees(e.target.checked);
              }}
            />
            Add fee (platform, transfer, bank charge…)
          </label>

          {hasFees && (
            <div className="mb-3 rounded-[var(--radius-control)] border border-border p-3">
              {feeRows.map((row, i) => (
                <div key={`fee-${String(i)}`} className="mb-2 flex gap-2">
                  <select
                    className={`${selectClass} flex-1`}
                    aria-label={`Fee ${String(i + 1)} type`}
                    value={row.type}
                    onChange={(e) => {
                      const value = e.target.value as FeeType | '';
                      setFeeRows((prev) =>
                        prev.map((f, j) => (j === i ? { ...f, type: value } : f)),
                      );
                    }}
                  >
                    <option value="">Fee type…</option>
                    {FEE_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t.replace('_', ' ')}
                      </option>
                    ))}
                  </select>
                  <div className="flex-1">
                    <AmountField
                      id={`tx-fee-amount-${String(i)}`}
                      ariaLabel={`Fee ${String(i + 1)} amount`}
                      currency={currency}
                      value={row.amount}
                      onChange={(v) => {
                        setFeeRows((prev) =>
                          prev.map((f, j) => (j === i ? { ...f, amount: v } : f)),
                        );
                      }}
                    />
                  </div>
                  {feeRows.length > 1 && (
                    <button
                      type="button"
                      aria-label={`Remove fee ${String(i + 1)}`}
                      className="text-muted hover:text-danger"
                      onClick={() => {
                        setFeeRows((prev) => prev.filter((_, j) => j !== i));
                      }}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="mb-2 flex items-center gap-1 text-sm text-primary hover:underline"
                onClick={() => {
                  setFeeRows((prev) => [...prev, { type: '', amount: '' }]);
                }}
              >
                <Plus size={14} /> Add fee
              </button>
              {netMinor !== null && (
                <p className="text-sm text-muted">
                  {txType === 'income' ? 'Net received' : 'Total including fees'}:{' '}
                  {formatMoney(fromMinor(currency, netMinor))}
                </p>
              )}
            </div>
          )}

          <button
            type="button"
            className={`${ghostButtonInlineClass} mb-3 w-full`}
            onClick={() => {
              setDetailed(false);
              setSplit(false);
              setHasFees(false);
            }}
          >
            Fewer fields
          </button>
        </>
      )}

      {error !== null && <p className="text-sm text-danger">{error}</p>}
    </FormSheet>
  );
}
